import { Field } from '@base-ui/react/field';
import { Form } from '@base-ui/react/form';
import { Input } from '@base-ui/react/input';
import { type SubmitEvent, useState } from 'react';
import logo from '@/assets/images/logo.webp';
import { Button } from '@/components/ui/Button';
import { cx } from '@/cva.config';

interface LoginProps {
  onSuccess: () => void;
}

export const Login = ({ onSuccess }: LoginProps) => {
  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');

  const hasError = Boolean(errors.root);

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const res = await fetch('/api/login', {
        body: JSON.stringify({ password }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (res.ok) {
        onSuccess();
      } else {
        setErrors({ root: 'Invalid password' });
        setPassword('');
      }
    } catch {
      setErrors({ root: 'Invalid password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Form
        className={cx(
          'w-full max-w-sm space-y-6 rounded-xl border border-border bg-surface p-8',
          hasError && 'animate-shake',
        )}
        errors={errors}
        onSubmit={handleSubmit}
      >
        <div className="text-center flex flex-col items-center gap-4">
          <img
            alt="Job Goblin"
            className="object-cover object-right"
            src={logo}
          />
          <p className="mt-1 text-sm lg:text-base text-foreground-muted">
            Enter password to continue
          </p>
        </div>
        <Field.Root name="root">
          <Input
            aria-label="Enter your password"
            autoComplete="current-password"
            autoFocus
            className={cx(
              'w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm lg:text-base transition-colors placeholder:text-foreground-muted/50 focus:border-accent focus:ring-2 focus:ring-accent/50 focus:outline-none',
              hasError && 'border-score-low',
            )}
            id="password"
            name="password"
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            type="password"
            value={password}
          />
          <Field.Error className="mt-2 text-xs lg:text-sm text-score-low" />
        </Field.Root>
        <Button
          className="py-2.5"
          disabled={!password}
          fullWidth
          loading={loading}
          size="lg"
          type="submit"
        >
          Sign In
        </Button>
      </Form>
    </div>
  );
};
